import { useMemo } from 'react'
import { GridOptions, ProcessCellForExportParams } from 'ag-grid-community'
import {
  ColumnType,
  columnTypes,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { intl } from '../../../../i18n'
import { ProgramProjectReportRow } from '../model/reportRow'
import { ProgramProjectReportNameCellRenderer } from '../components/ProgramProjectReportNameCellRenderer'
import { useColumnDefs } from './columnDefs'

export const processProgramProjectReportCellForClipboard = ({
  column,
  value,
  formatValue,
}: ProcessCellForExportParams<ProgramProjectReportRow>): string => {
  const { field } = column.getColDef()
  if ((field === 'createdBy' || field === 'updatedBy') && value) {
    return value.name ?? ''
  }
  return formatValue(value) ?? ''
}

const stripNumberGrouping = (value: string): string =>
  value.replace(/[,\s]/g, '')

export const processProgramProjectReportCellFromClipboard = ({
  column,
  value,
}: ProcessCellForExportParams<ProgramProjectReportRow>): string => {
  const { type } = column.getColDef()
  const types = Array.isArray(type) ? type : type ? [type] : []
  if (types.includes(ColumnType.number) && typeof value === 'string') {
    return stripNumberGrouping(value)
  }
  return value
}

export const useProgramProjectReportGridOptions =
  (): GridOptions<ProgramProjectReportRow> => {
    const columnDefs = useColumnDefs()

    return useMemo(
      () => ({
        groupHeaderHeight: 25,
        headerHeight: 45,
        floatingFiltersHeight: 25,
        rowHeight: 32,

        context: {},

        columnTypes: columnTypes(),
        components: frameworkComponents,
        defaultColGroupDef: { suppressHeaderContextMenu: true },
        defaultColDef: {
          cellDataType: false,
          width: 85,
          editable: false,
          enableValue: false,
          sortable: true,
          resizable: true,
          filter: 'clientSideTextFilter',
          floatingFilter: true,
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          suppressSizeToFit: true,
          cellStyle: { justifyContent: 'left' },
          cellRenderer: DefaultCellRenderer,
          onCellClicked: defaultOnCellClicked,
        },
        columnDefs,
        processCellForClipboard: processProgramProjectReportCellForClipboard,
        processCellFromClipboard: processProgramProjectReportCellFromClipboard,

        treeData: true,
        excludeChildrenWhenTreeDataFiltering: true,
        groupDefaultExpanded: -1,
        autoGroupColumnDef: {
          headerName: intl.formatMessage({
            id: 'programProjectReport.column.name',
          }),
          field: 'displayName',
          editable: false,
          sortable: true,
          filter: 'clientSideTextFilter',
          cellRendererParams: {
            suppressCount: true,
            innerRenderer: ProgramProjectReportNameCellRenderer,
          },
          width: 300,
          pinned: 'left' as const,
        },
        processUnpinnedColumns: () => [],
      }),
      [columnDefs]
    )
  }
